import {exhaustive} from '@freckle/exhaustive'
import type {ParserT} from '@freckle/parser'
import {literal, firstOf, record, tag, string, array, number} from '@freckle/parser'
import type {PointT, InequalityT} from '@freckle/educator-entities/ts/math/common/logic/equation'
import {parsePoint, parseInequality} from '@freckle/educator-entities/ts/math/common/logic/equation'

// Representation sent to the backend for assessments
export type InputAnswerT = string
export type MCAnswerT = number
export type MAAnswerT = Array<number>
export type BucketAnswerT = Array<Array<number>>
export type LinearGraphAnswerT = {
  points: Array<PointT>
}
export type QuadraticGraphAnswerT = {
  vertex: PointT
  point: PointT
}
export type ExponentialGraphAnswerT = {
  points: Array<PointT>
}
export type LinearInequalityGraphAnswerT = {
  points: Array<PointT>
  inequality: InequalityT
}
export type ScatterPointsGraphAnswerT = {
  points: Array<PointT>
}
export type FillInTheBlanksCellAnswerT = {
  answer: string
  column: number
  row: number
}
type FillInTheBlanksAnswerT = Array<FillInTheBlanksCellAnswerT>

type graph_linear = {
  tag: 'graph_linear'
  value: SavedLinearGraphAnswerT
}
type graph_quadratic = {
  tag: 'graph_quadratic'
  value: SavedQuadraticGraphAnswerT
}
type graph_exponential = {
  tag: 'graph_exponential'
  value: SavedExponentialGraphAnswerT
}
type graph_linear_inequality = {
  tag: 'graph_linear_inequality'
  value: SavedLinearInequalityGraphAnswerT
}
type graph_scatter_points = {
  tag: 'graph_scatter_points'
  value: SavedScatterPointsGraphAnswerT
}
type fill_in_the_blanks = {
  tag: 'fill_in_the_blanks'
  value: SavedFillInTheBlanksAnswerT
}

// The saved representation is the same except that SavedMCAnswerT,
// SavedMAAnswerT, and SavedBucketAnswerT have the actual answer instead
// of an index
type SavedInputAnswerT = InputAnswerT

type SavedMCAnswerT = string
type SavedMAAnswerT = Array<string>
export type SavedBucketAnswerT = Array<Array<string>>
type SavedLinearGraphAnswerT = LinearGraphAnswerT
type SavedQuadraticGraphAnswerT = QuadraticGraphAnswerT
type SavedExponentialGraphAnswerT = ExponentialGraphAnswerT
type SavedLinearInequalityGraphAnswerT = LinearInequalityGraphAnswerT
type SavedScatterPointsGraphAnswerT = ScatterPointsGraphAnswerT
export type SavedFillInTheBlanksAnswerT = FillInTheBlanksAnswerT

export type TaggedSavedAnswerT =
  | {
      tag: 'singular_or'
      value: SavedInputAnswerT
    }
  | {
      tag: 'multiple_choice'
      value: SavedMCAnswerT
    }
  | {
      tag: 'multiple_answers'
      value: SavedMAAnswerT
    }
  | {
      tag: 'bucket'
      value: SavedBucketAnswerT
    }
  | {
      tag: 'graph_linear'
      value: SavedLinearGraphAnswerT
    }
  | {
      tag: 'graph_quadratic'
      value: SavedQuadraticGraphAnswerT
    }
  | {
      tag: 'graph_exponential'
      value: SavedExponentialGraphAnswerT
    }
  | {
      tag: 'graph_linear_inequality'
      value: SavedLinearInequalityGraphAnswerT
    }
  | {
      tag: 'graph_scatter_points'
      value: SavedScatterPointsGraphAnswerT
    }
  | {
      tag: 'fill_in_the_blanks'
      value: SavedFillInTheBlanksAnswerT
    }

export const parseTaggedSavedAnswer: ParserT<TaggedSavedAnswerT> = firstOf(
  record({tag: tag('singular_or'), value: string()}),
  record({tag: tag('multiple_choice'), value: string()}),
  record({tag: tag('multiple_answers'), value: array(string())}),
  record({tag: tag('bucket'), value: array(array(string()))}),
  record({
    tag: tag('graph_linear'),
    value: record({
      points: array(parsePoint)
    })
  }) as ParserT<graph_linear>,
  record({
    tag: tag('graph_quadratic'),
    value: record({
      vertex: parsePoint,
      point: parsePoint
    })
  }) as ParserT<graph_quadratic>,
  record({
    tag: tag('graph_exponential'),
    value: record({
      points: array(parsePoint)
    })
  }) as ParserT<graph_exponential>,
  record({
    tag: tag('graph_linear_inequality'),
    value: record({
      points: array(parsePoint),
      inequality: parseInequality
    })
  }) as ParserT<graph_linear_inequality>,
  record({
    tag: tag('graph_scatter_points'),
    value: record({
      points: array(parsePoint)
    })
  }) as ParserT<graph_scatter_points>,
  record({
    tag: tag('fill_in_the_blanks'),
    value: array(
      record({
        row: number(),
        column: number(),
        answer: string()
      })
    )
  }) as ParserT<fill_in_the_blanks>
)

export type AnswerType =
  | 'singular_or'
  | 'multiple_choice'
  | 'multiple_answers'
  | 'bucket'
  | 'graph_linear'
  | 'graph_quadratic'
  | 'graph_exponential'
  | 'graph_linear_inequality'
  | 'graph_scatter_points'
  | 'fill_in_the_blanks'

export const parseAnswerType: ParserT<AnswerType> = firstOf(
  literal('singular_or'),
  literal('multiple_choice'),
  literal('multiple_answers'),
  literal('bucket'),
  literal('graph_linear'),
  literal('graph_quadratic'),
  literal('graph_exponential'),
  literal('graph_linear_inequality'),
  literal('graph_scatter_points'),
  literal('fill_in_the_blanks')
)

export const AnswerTypes = {
  SingularOr: 'singular_or' as AnswerType,
  MultipleChoice: 'multiple_choice' as AnswerType,
  MultipleAnswers: 'multiple_answers' as AnswerType,
  Bucket: 'bucket' as AnswerType,
  GraphLinear: 'graph_linear' as AnswerType,
  GraphQuadratic: 'graph_quadratic' as AnswerType,
  GraphExponential: 'graph_exponential' as AnswerType,
  GraphLinearInequality: 'graph_linear_inequality' as AnswerType,
  GraphScatterPoints: 'graph_scatter_points' as AnswerType,
  FillInTheBlanks: 'fill_in_the_blanks' as AnswerType,

  all: function (): Array<AnswerType> {
    return [
      'singular_or',
      'multiple_choice',
      'multiple_answers',
      'bucket',
      'graph_linear',
      'graph_quadratic',
      'graph_exponential',
      'graph_linear_inequality',
      'graph_scatter_points',
      'fill_in_the_blanks'
    ]
  }
}

export type TaggedAnswerT =
  | {
      kind: 'singular_or'
      answer: InputAnswerT
    }
  | {
      kind: 'multiple_choice'
      answer: MCAnswerT
    }
  | {
      kind: 'multiple_answers'
      answer: MAAnswerT
    }
  | {
      kind: 'bucket'
      answer: BucketAnswerT
    }
  | {
      kind: 'graph_linear'
      answer: LinearGraphAnswerT
    }
  | {
      kind: 'graph_quadratic'
      answer: QuadraticGraphAnswerT
    }
  | {
      kind: 'graph_exponential'
      answer: ExponentialGraphAnswerT
    }
  | {
      kind: 'graph_linear_inequality'
      answer: LinearInequalityGraphAnswerT
    }
  | {
      kind: 'graph_scatter_points'
      answer: ScatterPointsGraphAnswerT
    }
  | {
      kind: 'fill_in_the_blanks'
      answer: FillInTheBlanksAnswerT
    }

// Formatters for TaggedAnswerT answers

type AnswerPlacementT = 'inline-question' | 'bottom-full-width'

export function getAnswerTypePlacement(answerType: AnswerType): AnswerPlacementT {
  switch (answerType) {
    case 'graph_linear':
    case 'graph_quadratic':
    case 'graph_exponential':
    case 'graph_linear_inequality':
    case 'graph_scatter_points':
    case 'fill_in_the_blanks':
      return 'inline-question'
    case 'bucket':
    case 'multiple_answers':
    case 'multiple_choice':
    case 'singular_or':
      return 'bottom-full-width'
    default:
      return exhaustive(answerType, `AnswerTypeT`)
  }
}
